import type { VeoAutocaptureConfig, VeoClient } from '../../types';
import { hasDocument } from '../../utils/safe-env';
import { DEFAULT_AUTOCAPTURE_CONFIG, MAX_ANCESTORS } from './constants';
import { buildAutocapturePayload } from './element-serializer';
import { isElementBlocked } from './pii-guard';
import { ElementThrottle } from './throttle';

type AutocaptureEvent = 'click' | 'submit' | 'change';

/**
 * Instala listeners delegados en `document` y traduce las interacciones del
 * usuario en eventos `$autocapture`. Nunca lanza hacia la app del cliente:
 * cada handler está envuelto en try/catch.
 */
export class DomListener {
  private readonly throttle: ElementThrottle;
  private readonly config: Required<VeoAutocaptureConfig>;
  private cleanups: Array<() => void> = [];

  constructor(
    private readonly client: VeoClient,
    config: VeoAutocaptureConfig,
  ) {
    const d = DEFAULT_AUTOCAPTURE_CONFIG;
    // Resolver el config de forma explícita: el merge por spread reintroduce
    // `| undefined` y no satisface `Required<VeoAutocaptureConfig>`.
    this.config = {
      enabled: config.enabled ?? d.enabled,
      captureClicks: config.captureClicks ?? d.captureClicks,
      captureSubmits: config.captureSubmits ?? d.captureSubmits,
      captureChanges: config.captureChanges ?? d.captureChanges,
      maskAllInputs: config.maskAllInputs ?? d.maskAllInputs,
      maskAllText: config.maskAllText ?? d.maskAllText,
      blockSelectors: config.blockSelectors ?? [],
      throttleMs: config.throttleMs ?? d.throttleMs,
      maxTextLength: config.maxTextLength ?? d.maxTextLength,
      captureElementMetadata: config.captureElementMetadata ?? d.captureElementMetadata,
      captureAncestors: Math.min(config.captureAncestors ?? d.captureAncestors, MAX_ANCESTORS),
    };
    this.throttle = new ElementThrottle(this.config.throttleMs);
  }

  install(): void {
    if (!hasDocument() || !this.config.enabled) return;

    if (this.config.captureClicks) this.attach('click', (e) => this.handleClick(e));
    if (this.config.captureSubmits) this.attach('submit', (e) => this.handleSubmit(e), true);
    if (this.config.captureChanges) this.attach('change', (e) => this.handleChange(e), true);
  }

  destroy(): void {
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups = [];
  }

  private attach(
    eventName: AutocaptureEvent,
    handler: (e: Event) => void,
    useCapture = false,
  ): void {
    const listener = (e: Event) => {
      // Solo eventos reales del usuario (los sintéticos tienen isTrusted=false).
      if (!e.isTrusted) return;
      try {
        handler(e);
      } catch {
        // Nunca romper la app del cliente; descartamos el evento silenciosamente.
      }
    };
    document.addEventListener(eventName, listener, useCapture);
    this.cleanups.push(() => document.removeEventListener(eventName, listener, useCapture));
  }

  private handleClick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Subir hasta el elemento "interesante" (botón, link, [role], etc.).
    // Si NO hay ninguno (click en zona vacía/no interactiva, p. ej. <html>),
    // no emitimos: evita ruido y volumen innecesario de eventos.
    const meaningful = this.findMeaningfulAncestor(target);
    if (!meaningful) return;
    if (isElementBlocked(meaningful, this.config.blockSelectors)) return;
    if (!this.throttle.shouldFire(meaningful)) return;

    this.emit('click', meaningful);
  }

  private handleSubmit(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element) || target.tagName.toLowerCase() !== 'form') return;
    if (isElementBlocked(target, this.config.blockSelectors)) return;
    if (!this.throttle.shouldFire(target)) return;

    this.emit('submit', target);
  }

  private handleChange(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tag = target.tagName.toLowerCase();
    if (tag !== 'select' && tag !== 'input' && tag !== 'textarea') return;

    // Para text inputs el change sería spam/PII; solo checkbox/radio (y selects).
    if (
      target instanceof HTMLInputElement &&
      target.type !== 'checkbox' &&
      target.type !== 'radio'
    ) {
      return;
    }
    if (isElementBlocked(target, this.config.blockSelectors)) return;
    if (!this.throttle.shouldFire(target)) return;

    this.emit('change', target);
  }

  /** Busca hacia arriba un ancestro "interactivo". Devuelve null si no hay ninguno. */
  private findMeaningfulAncestor(element: Element): Element | null {
    let current: Element | null = element;
    let depth = 0;

    while (current && current !== document.documentElement && depth < 5) {
      const tag = current.tagName.toLowerCase();
      const isInteractive =
        tag === 'button' ||
        tag === 'a' ||
        tag === 'input' ||
        tag === 'select' ||
        tag === 'textarea' ||
        current.hasAttribute('data-veo-tag') ||
        current.hasAttribute('role') ||
        current.hasAttribute('onclick') ||
        (current instanceof HTMLElement && current.style.cursor === 'pointer');

      if (isInteractive) return current;
      current = current.parentElement;
      depth++;
    }

    return null;
  }

  private emit(eventType: AutocaptureEvent, element: Element): void {
    const payload = buildAutocapturePayload(eventType, element, this.config);
    this.client.track('$autocapture', payload as unknown as Record<string, unknown>);
  }
}
